import { useState } from 'react'
import type { JSX } from 'react'

import { Temporal } from '@js-temporal/polyfill'
import { format } from 'date-fns'

import {
  Button,
  Calendar,
  CalendarIcon,
  ChevronDownIcon,
  Drawer,
  Separator,
  ToggleGroup,
} from '@buffer-mono/popcorn'

import { useTimezone } from '~publish/hooks/useTimezone'
import { useWeekStartsOn } from '~publish/hooks/useWeekStartsOn'

import { useCalendar } from '../../../components/Calendar/hooks/useCalendar'
import {
  isCalendarViewMode,
  VIEW_MODE_OPTIONS,
} from '../../../components/Calendar/hooks/useCalendarDateRangeViewMode'

import styles from './MobileCalendarDateLabel.module.css'

const START_MONTH = new Temporal.PlainDate(2015, 1, 1)

export function MobileCalendarDateLabel({
  className,
}: {
  className?: string
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const timezone = useTimezone()
  const weekStartsOn = useWeekStartsOn() === 'monday' ? 1 : 0
  const {
    selectedDate,
    viewMode,
    navigateToDate,
    navigateToToday,
    toggleViewMode,
  } = useCalendar({ timezone })

  const dateString = format(selectedDate.toPlainDateTime().toString(), 'MMMM d')

  function handleDateSelect(plainDate: Temporal.PlainDate | undefined): void {
    if (!plainDate) return
    const zonedDate = Temporal.ZonedDateTime.from({
      year: plainDate.year,
      month: plainDate.month,
      day: plainDate.day,
      timeZone: timezone,
    })
    navigateToDate(zonedDate)
    setOpen(false)
  }

  function handleTodayClick(): void {
    navigateToToday()
    setOpen(false)
  }

  function handleViewModeChange(value: string): void {
    if (isCalendarViewMode(value)) {
      toggleViewMode(value)
    }
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <Drawer.Trigger>
        <Button as="h2" variant="tertiary" size="small" className={className}>
          {dateString} <ChevronDownIcon />
        </Button>
      </Drawer.Trigger>
      <Drawer.Content className={styles.drawerContent}>
        <Drawer.Header className={styles.header}>
          <Drawer.Title>Calendar</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className={styles.body}>
          <ToggleGroup
            value={viewMode}
            onChange={handleViewModeChange}
            className={styles.toggleGroup}
          >
            {VIEW_MODE_OPTIONS.map(([value, label]) => (
              <ToggleGroup.Item key={value} value={value}>
                {label}
              </ToggleGroup.Item>
            ))}
          </ToggleGroup>
          <div className={styles.calendarContainer}>
            <Calendar
              value={selectedDate.toPlainDate()}
              onValueChange={handleDateSelect}
              min={null}
              startMonth={START_MONTH}
              timezone={timezone}
              weekStartsOn={weekStartsOn}
            />
            <Separator />
            <Button variant="tertiary" onClick={handleTodayClick}>
              <CalendarIcon /> Today
            </Button>
          </div>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer>
  )
}
